import { YStack } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { EmailSchema } from '@/features/user/user.types';

export function isPrivateRelayEmail(email?: string | null) {
  return (
    email?.trim().toLowerCase().endsWith('@privaterelay.appleid.com') ?? false
  );
}

export function getFollowUpEmailError(followUpEmail: string) {
  const normalizedFollowUpEmail = followUpEmail.trim().toLowerCase();
  if (!normalizedFollowUpEmail) return null;

  const result = EmailSchema.safeParse(normalizedFollowUpEmail);
  if (!result.success) {
    return (
      result.error.issues[0]?.message || 'Please enter a valid email address'
    );
  }

  if (isPrivateRelayEmail(normalizedFollowUpEmail)) {
    return 'Please use an email we can reply to (not an Apple private relay address)';
  }

  return null;
}

interface FeedbackFollowUpEmailProps {
  userEmail?: string | null;
  followUpEmail: string;
  setFollowUpEmail: (value: string) => void;
}

export function FeedbackFollowUpEmail({
  userEmail,
  followUpEmail,
  setFollowUpEmail,
}: FeedbackFollowUpEmailProps) {
  if (!isPrivateRelayEmail(userEmail)) return null;

  const followUpEmailError = getFollowUpEmailError(followUpEmail);

  return (
    <YStack className="w-full gap-3 rounded-xl border border-border bg-surface px-3.5 py-3">
      <YStack className="gap-1">
        <span className="text-sm font-medium text-foreground">
          Best email for follow-up
        </span>
        <span className="text-xs text-foreground-muted">
          You&apos;re signed in with Apple&apos;s private relay email. Add
          another address if you&apos;d like us to be able to respond.
        </span>
      </YStack>
      <Input
        aria-label="Best email for follow-up"
        type="email"
        variant="onSurface"
        value={followUpEmail}
        onChange={(event) => setFollowUpEmail(event.target.value)}
        placeholder="name@example.com"
        autoComplete="email"
        inputMode="email"
        spellCheck={false}
        error={followUpEmailError ?? undefined}
      />
    </YStack>
  );
}
